import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { theme } from '../theme';
import primaryLogo from '../assets/3.png';
import secondaryLogo from '../assets/2.png';
import dashboardOverviewImg from '../assets/dashboard-overview.png';
import dailyStatsImg from '../assets/daily-stats.png';
import allTradesImg from '../assets/all-trades.png';
import reportsInsightsImg from '../assets/reports-insights.png';
import importsImg from '../assets/imports.png';
import weeklyReviewImg from '../assets/weekly-review.png';
import iosScreenshotImg from '../assets/tbss.PNG';

const screenshotItems = [
  {
    title: 'Dashboard Overview',
    description: 'Daily P&L, win rate, streaks, and performance trend at a glance.',
    src: dashboardOverviewImg,
  },
  {
    title: 'Daily Stats',
    description: 'Break down each session to see what made or broke the day.',
    src: dailyStatsImg,
  },
  {
    title: 'All Trades',
    description: 'Review each trade with notes, ratings, and setup/mistake tags.',
    src: allTradesImg,
  },
  {
    title: 'Reports & Insights',
    description: 'Find patterns by setup, symbol, day, and execution behavior.',
    src: reportsInsightsImg,
  },
  {
    title: 'Weekly Review',
    description: 'Turn weekly performance into focused, actionable goals.',
    src: weeklyReviewImg,
  },
  {
    title: 'Imports',
    description: 'Connect brokers or import files to keep your journal current.',
    src: importsImg,
  },
];


const faqItems = [
  {
    q: 'What brokers can I connect to TradeBetter?',
    a: 'TradeBetter currently supports direct OAuth connections with Charles Schwab and Webull, which automatically sync your recent trades. You can also manually import CSV exports from thinkorswim (TD Ameritrade) and Interactive Brokers (IBKR Activity Statements). Additional broker support is on our roadmap.',
  },
  {
    q: 'What file formats are supported for importing trades?',
    a: 'We support CSV exports from thinkorswim and IBKR Activity Statements out of the box — just drop the file in and TradeBetter auto-detects the format. For brokers with OAuth support (Schwab, Webull), you can skip the file entirely and sync directly from your account.',
  },
  {
    q: 'Can I cancel my subscription at any time?',
    a: 'Yes — no contracts, no cancellation fees. For web subscriptions, cancel from Settings → Subscription and your access continues through the end of the current billing period. For iOS subscriptions, manage billing through your Apple ID in the App Store. Your data stays intact either way.',
  },
  {
    q: "What's the difference between Basic and Pro?",
    a: 'Basic gives you full access to trade imports, the dashboard, daily stats, all-trades view, and reports — everything you need to analyze your trading history. Pro adds all four AI features (Trade Review, Daily Debrief, Pattern Detection, and Weekly Review), unlimited broker connections, and priority support.',
  },
  {
    q: 'How does the AI work and what can it actually tell me?',
    a: 'TradeBetter uses GPT-4o to analyze your actual trade data — not generic advice. The AI Trade Review coaches you on individual trades, Daily Debrief summarizes your session against your stated goals, Pattern Detection surfaces behavioral trends across your full history, and Weekly Review turns your numbers into a focused plan for the week ahead. All AI features are Pro-only.',
  },
  {
    q: 'Is my trading data private and secure?',
    a: 'Yes. Your trade data is stored in your own private Firestore document and is never shared with other users or sold to third parties. Broker OAuth tokens are encrypted at rest. TradeBetter never has direct access to execute trades — connections are read-only for transaction history.',
  },
  {
    q: 'Can I use TradeBetter on my iPhone or iPad?',
    a: 'Yes — TradeBetter is available as a native iOS app on the App Store, built for iPhone and iPad. The iOS app is in sync with the web app and uses the same account, so your journal is available across all your devices.',
  },
  {
    q: 'Does TradeBetter support stocks and futures, or just options?',
    a: 'TradeBetter is purpose-built for options traders. The trade grouping, P&L computation, and analytics are all designed around options round-trips (entry to exit by symbol, strike, expiry, and type). Stock and futures imports may parse but the analysis is optimized for options.',
  },
  {
    q: 'Can I export my journal data?',
    a: 'Your trade data can be exported directly from the Imports screen. We provide CSV export so you always have a portable copy of your journal — no lock-in.',
  },
  {
    q: 'How often should I sync my broker account?',
    a: "Schwab and Webull syncs pull the last 60 days of transactions each time, so syncing after each trading session or once a day keeps your journal current. The sync uses a unique activity ID to deduplicate, so running it multiple times won't create duplicate trades.",
  },
  {
    q: 'What happens to my data if I cancel my subscription?',
    a: 'Your account and all trade data remain intact. You lose access to Pro features (AI and multiple broker connections), but everything you imported stays in your journal. You can resubscribe at any time and pick up right where you left off.',
  },
  {
    q: 'How do I cancel my subscription?',
    a: 'Go to Settings → Subscription and click "Manage Subscription." For web (Stripe) billing, cancellation takes effect at the end of your current billing period. For iOS subscribers, manage billing directly through your Apple ID in the App Store settings.',
  },
];

const MarketingLanding = () => {
  usePageMeta({
    title: 'TradeBetter — AI Options Trading Journal | Schwab, Webull & thinkorswim',
    description: 'Track, analyze, and improve your options trades with AI coaching. Connect Schwab, Webull, or import from thinkorswim & IBKR. Win rate by setup, pattern detection, and weekly AI reviews.',
    canonical: 'https://tradebetter.net/',
  });
  const navigate = useNavigate();
  const { currentUser, displayName, logout } = useAuth();
  const [activeShotIndex, setActiveShotIndex] = useState(0);
  const activeShot = screenshotItems[activeShotIndex];
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const visibleFaqs = faqItems.slice(0, 6);

  return (
    <div className="ml-root">
      <style>
        {`
          @keyframes pulseGlow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .brandText {
            color: #34d399;
          }

          .ml-root {
            min-height: 100vh;
            color: ${theme.colors.white};
            background:
              radial-gradient(ellipse 80% 50% at 20% -10%, rgba(52, 211, 153, 0.07) 0%, transparent 60%),
              radial-gradient(ellipse 60% 40% at 80% 10%, rgba(96, 165, 250, 0.07) 0%, transparent 55%),
              radial-gradient(ellipse 50% 60% at 50% 100%, rgba(45, 212, 191, 0.04) 0%, transparent 60%),
              #06080f;
          }

          .ml-shell {
            max-width: 1320px;
            margin: 0 auto;
            padding: 0 24px;
          }

          .ml-nav {
            position: sticky;
            top: 0;
            z-index: 20;
            backdrop-filter: blur(10px);
            background: rgba(8, 12, 20, 0.82);
            border-bottom: 1px solid rgba(140, 169, 201, 0.16);
          }

          .ml-nav-row {
            height: 78px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
          }

          .ml-brand {
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .ml-brand-icon {
            width: 46px;
            height: 46px;
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid rgba(123, 160, 203, 0.25);
            background: rgba(15, 20, 32, 0.7);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5), 0 0 24px rgba(0, 123, 255, 0.24);
          }

          .ml-brand-name {
            font-weight: 700;
            letter-spacing: 0.3px;
            font-size: 21px;
            color: #f2f7ff;
          }

          /* ── Hero CTA buttons ── */
          .ml-btn {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            border: 1px solid rgba(255,255,255,0.1);
            background: transparent;
            color: #8fa8c4;
            padding: 11px 20px;
            border-radius: 10px;
            font-size: 14.5px;
            font-weight: 500;
            cursor: pointer;
            transition: border-color 0.18s ease, color 0.18s ease;
            font-family: inherit;
          }

          .ml-btn:hover {
            border-color: rgba(255,255,255,0.2);
            color: #c8d8ec;
          }

          .ml-btn-primary {
            border: none;
            background: #34d399;
            color: #021a10;
            font-weight: 700;
            font-size: 14.5px;
            padding: 11px 24px;
            letter-spacing: 0.1px;
          }

          .ml-btn-primary:hover {
            background: #2ec68e;
          }

          /* ── Navbar buttons ── */
          .ml-nav-link {
            background: none;
            border: none;
            color: #8899aa;
            padding: 7px 12px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: color 0.18s ease, background 0.18s ease;
            font-family: inherit;
            letter-spacing: 0.1px;
          }

          .ml-nav-link:hover {
            color: #dde8f5;
            background: rgba(255,255,255,0.05);
          }

          .ml-nav-login {
            display: inline-flex;
            align-items: center;
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(130, 155, 185, 0.25);
            color: #c4d4e8;
            padding: 8px 18px;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: inherit;
          }

          .ml-nav-login:hover {
            background: rgba(255,255,255,0.08);
            border-color: rgba(130, 155, 185, 0.45);
            color: #e8f0fb;
            transform: translateY(-1px);
          }

          .ml-nav-cta {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            border: none;
            background: linear-gradient(135deg, #1ecf97 0%, #2db9ff 100%);
            color: #021a12;
            padding: 9px 20px;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.22s ease;
            font-family: inherit;
            letter-spacing: 0.15px;
            box-shadow: 0 4px 14px rgba(30,207,151,0.28), 0 8px 24px rgba(45,185,255,0.18);
          }

          .ml-nav-cta:hover {
            transform: translateY(-1px);
            filter: brightness(1.07);
            box-shadow: 0 6px 20px rgba(30,207,151,0.36), 0 12px 32px rgba(45,185,255,0.24);
          }

          .ml-nav-signout {
            background: none;
            border: none;
            color: #6b7f96;
            padding: 7px 12px;
            border-radius: 8px;
            font-size: 13px;
            cursor: pointer;
            transition: color 0.18s ease;
            font-family: inherit;
          }

          .ml-nav-signout:hover {
            color: #a0b3c8;
          }

          .ml-hero {
            padding: 72px 0 80px;
            display: grid;
            grid-template-columns: 1fr 1.55fr;
            gap: 48px;
            align-items: center;
          }

          .ml-hero-center {
            animation: fadeUp 0.5s ease both;
          }

          .ml-hero-visual-col {
            animation: fadeUp 0.5s ease 0.12s both;
          }

          .ml-hero-frame {
            border-radius: 12px;
            overflow: hidden;
            border: 1px solid rgba(255,255,255,0.07);
            box-shadow: 0 32px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.03) inset;
            background: #070d17;
          }

          .ml-hero-frame-chrome {
            height: 34px;
            background: rgba(255,255,255,0.025);
            border-bottom: 1px solid rgba(255,255,255,0.055);
            display: flex;
            align-items: center;
            padding: 0 14px;
            gap: 6px;
          }

          .ml-hero-frame-dot {
            width: 9px;
            height: 9px;
            border-radius: 50%;
          }

          .ml-hero-frame-img {
            width: 100%;
            display: block;
          }

          /* ── Dual preview (phone + desktop) ── */
          .ml-hero-dual-preview {
            position: relative;
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;
            gap: 20px;
          }

          /* ══════════════════════════════
             iPhone 15 Pro — realistic frame
             ══════════════════════════════ */
          .ml-phone-mockup {
            position: relative;
            flex-shrink: 0;
            z-index: 2;
            width: 260px;

            /* Titanium-finish frame gradient */
            background: linear-gradient(
              148deg,
              #4a4a4c 0%,
              #2e2e30 18%,
              #1c1c1e 45%,
              #111113 70%,
              #1a1a1c 100%
            );

            /* outer-radius = inner-radius + padding (8px) so corners align perfectly */
            border-radius: 54px;
            padding: 8px;

            /* Layered shadows:
               1) hair-line inner highlight (top-left rim)
               2) thin outer edge
               3) ambient + key depth shadows */
            box-shadow:
              inset 0 1px 0 rgba(255,255,255,0.22),
              inset -1px 0 0 rgba(255,255,255,0.06),
              0 0 0 0.5px rgba(0,0,0,0.9),
              0 2px 4px rgba(0,0,0,0.5),
              0 20px 60px rgba(0,0,0,0.75),
              0 50px 120px rgba(0,0,0,0.5);

            transform: rotate(-4deg) translateY(14px);
          }

          /* ── Side buttons via pseudo-elements ── */

          /* Right side: power button */
          .ml-phone-mockup::after {
            content: '';
            position: absolute;
            right: -3px;
            top: 28%;
            width: 3px;
            height: 64px;
            background: linear-gradient(to right, #1c1c1e, #3a3a3c 40%, #2a2a2c);
            border-radius: 0 3px 3px 0;
            box-shadow: inset -1px 0 0 rgba(255,255,255,0.12);
          }

          /* Left side: volume buttons (silent + vol up + vol down) */
          .ml-phone-mockup::before {
            content: '';
            position: absolute;
            left: -3px;
            top: 18%;
            width: 3px;
            /* Three buttons as a gap-striped gradient */
            height: 108px;
            background: linear-gradient(
              to bottom,
              #2a2a2c 0px, #3a3a3c 2px, #2a2a2c 22px,   /* mute */
              transparent 22px, transparent 30px,
              #2a2a2c 30px, #3a3a3c 32px, #2a2a2c 62px,   /* vol up */
              transparent 62px, transparent 70px,
              #2a2a2c 70px, #3a3a3c 72px, #2a2a2c 108px   /* vol down */
            );
            border-radius: 3px 0 0 3px;
            box-shadow: inset 1px 0 0 rgba(255,255,255,0.10);
          }

          /* ── Screen area — border-radius = 54 - 8 = 46px ── */
          .ml-phone-screen-wrap {
            border-radius: 46px;
            overflow: hidden;
            background: #000;
            position: relative;
          }

          /* ── Dynamic Island ── */
          .ml-phone-island {
            position: absolute;
            top: 12px;
            left: 50%;
            transform: translateX(-50%);
            /* ~108pt at real iPhone scale → proportioned to 260px width */
            width: 104px;
            height: 30px;
            background: #000;
            border-radius: 50px;
            z-index: 10;
            /* Faint inner glow to separate from screen */
            box-shadow:
              0 0 0 1px rgba(60,60,62,0.8),
              0 2px 6px rgba(0,0,0,0.9);
          }

          .ml-phone-screen {
            width: 100%;
            display: block;
          }

          /* Home indicator — rendered inside the phone frame below the screen */
          .ml-phone-home-bar {
            width: 88px;
            height: 4px;
            background: rgba(255,255,255,0.30);
            border-radius: 10px;
            margin: 9px auto 3px;
          }

          /* ── Desktop browser frame ── */
          .ml-desktop-preview {
            flex: 1;
            min-width: 0;
            position: relative;
            z-index: 1;
            transform: rotate(2deg) translate(32px, -80px) scale(1.18);
            border-radius: 14px;
            overflow: hidden;
            border: 1px solid rgba(255,255,255,0.10);
            background: #070d17;
            box-shadow:
              inset 0 1px 0 rgba(255,255,255,0.08),
              0 0 0 0.5px rgba(0,0,0,0.8),
              0 24px 60px rgba(0,0,0,0.7),
              0 60px 120px rgba(0,0,0,0.45);
          }

          .ml-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 6px 14px 6px 6px;
            border-radius: 999px;
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.08);
            width: fit-content;
            margin-bottom: 20px;
          }

          .ml-eyebrow-avatars {
            display: flex;
            align-items: center;
          }

          .ml-eyebrow-avatar {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            border: 2px solid #06080f;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-weight: 700;
            color: #fff;
            position: relative;
            flex-shrink: 0;
          }

          .ml-eyebrow-avatar:not(:first-child) {
            margin-left: -8px;
          }

          .ml-eyebrow-text {
            font-size: 12.5px;
            font-weight: 500;
            color: #8fa8c4;
            letter-spacing: 0.1px;
            white-space: nowrap;
          }

          .ml-eyebrow-stars {
            display: flex;
            align-items: center;
            gap: 1px;
          }

          .ml-eyebrow-dot {
            display: none;
          }

          .ml-title {
            margin: 0 0 18px;
            font-size: clamp(36px, 5.2vw, 56px);
            line-height: 1.05;
            letter-spacing: -1.8px;
            font-weight: 800;
            max-width: 16ch;
            color: #eef4ff;
          }

          .ml-title-line1 {
            display: block;
            font-size: 0.58em;
            font-weight: 600;
            letter-spacing: -0.5px;
            color: #8fa8c4;
            margin-bottom: 4px;
          }

          .ml-title-accent {
            background: linear-gradient(120deg, #34d399 0%, #60a5fa 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .ml-sub {
            color: #627b96;
            font-size: 16px;
            line-height: 1.72;
            max-width: 44ch;
            margin: 0 0 32px;
          }

          .ml-cta-row {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .ml-trust-row {
            margin-top: 18px;
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
          }

          .ml-trust-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12.5px;
            color: #627b96;
            font-weight: 500;
          }

          .ml-trust-check {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: rgba(52, 211, 153, 0.12);
            border: 1px solid rgba(52, 211, 153, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .ml-note {
            margin-top: 14px;
            color: #7d90aa;
            font-size: 13px;
          }

          .ml-proof-row {
            margin-top: 24px;
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }

          .ml-chip {
            border-radius: 999px;
            font-size: 12px;
            color: #bdd1ea;
            border: 1px solid rgba(122, 149, 181, 0.35);
            background: rgba(12, 18, 30, 0.72);
            padding: 6px 11px;
          }

          .ml-hero-visual-wrap {
            position: relative;
          }

          .ml-spark-path {
            stroke-dasharray: 420;
            stroke-dashoffset: 420;
            animation: sparkDraw 2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            animation-delay: 0.6s;
          }

          .ml-sparkline-wrap {
            margin-bottom: 14px;
            padding: 11px 13px;
            border-radius: 13px;
            background: rgba(255,255,255,0.022);
            border: 1px solid rgba(255,255,255,0.05);
          }

          .ml-sparkline-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
          }

          .ml-sparkline-label {
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.55px;
            color: #3d5a74;
            font-weight: 700;
          }

          .ml-sparkline-value {
            font-size: 14px;
            font-weight: 700;
            color: #34d399;
            letter-spacing: -0.3px;
          }

          .ml-rating-row {
            display: flex;
            align-items: center;
            gap: 5px;
            font-size: 12.5px;
            color: #627b96;
            font-weight: 500;
          }

          .ml-hero-glow {
            display: none;
          }

          .ml-hero-visual {
            display: none;
          }

          .ml-visual-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 9px;
          }

          .ml-kpi {
            border-radius: 14px;
            background: rgba(8, 13, 24, 0.72);
            border: 1px solid rgba(255,255,255,0.06);
            padding: 14px 16px;
            transition: border-color 0.2s ease;
          }

          .ml-kpi:hover {
            border-color: rgba(255,255,255,0.1);
          }

          .ml-kpi-label {
            font-size: 10.5px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            color: #5a7290;
            font-weight: 600;
          }

          .ml-kpi-value {
            margin-top: 8px;
            font-size: 24px;
            font-weight: 700;
            letter-spacing: -0.5px;
            line-height: 1;
          }

          .ml-kpi-sub {
            margin-top: 5px;
            font-size: 11.5px;
            color: #4e6880;
          }

          /* ── Stat band ── */
          .ml-stat-band {
            margin-top: 36px;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
          }

          .ml-stat {
            border-radius: 14px;
            background: rgba(9, 14, 25, 0.72);
            border: 1px solid rgba(109, 129, 156, 0.2);
            padding: 18px 20px;
            border-left: 2px solid rgba(45, 212, 191, 0.35);
            transition: border-color 0.2s ease, background 0.2s ease;
          }

          .ml-stat:hover {
            background: rgba(9, 14, 25, 0.9);
            border-left-color: rgba(45, 212, 191, 0.65);
          }

          .ml-stat strong {
            display: block;
            font-size: 16px;
            font-weight: 700;
            margin-bottom: 6px;
            color: #ddeaf8;
            letter-spacing: -0.2px;
          }

          .ml-stat span {
            color: #5a7290;
            font-size: 13px;
            line-height: 1.55;
          }

          /* ── Section layout ── */
          .ml-section {
            margin-top: 96px;
          }

          .ml-section-header {
            text-align: center;
            max-width: 640px;
            margin: 0 auto 48px;
          }

          .ml-section-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            padding: 6px 14px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0.4px;
            color: #2DD4BF;
            background: rgba(45, 212, 191, 0.08);
            border: 1px solid rgba(45, 212, 191, 0.22);
            margin-bottom: 16px;
          }

          .ml-h2 {
            font-size: clamp(26px, 4.1vw, 40px);
            line-height: 1.08;
            margin: 0 0 14px;
            letter-spacing: -0.7px;
            color: #eaf2ff;
          }

          .ml-p {
            color: #6b87a4;
            font-size: 17px;
            line-height: 1.72;
            margin: 0;
          }

          /* ── Feature cards ── */
          .ml-feature-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
          }

          .ml-feature-card {
            position: relative;
            border-radius: 18px;
            padding: 28px 26px;
            border: 1px solid rgba(255,255,255,0.06);
            background: linear-gradient(160deg, rgba(13, 19, 34, 0.88) 0%, rgba(8, 12, 22, 0.94) 100%);
            box-shadow: 0 20px 40px rgba(0,0,0,0.28);
            overflow: hidden;
            transition: border-color 0.25s ease, transform 0.2s ease;
          }

          .ml-feature-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            border-radius: 18px 18px 0 0;
            background: var(--card-accent, linear-gradient(90deg, #2dd4bf, #60a5fa));
            opacity: 0.7;
          }

          .ml-feature-card:hover {
            border-color: rgba(255,255,255,0.1);
            transform: translateY(-2px);
          }

          .ml-feature-icon {
            width: 42px;
            height: 42px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 18px;
            flex-shrink: 0;
          }

          .ml-feature-card h3 {
            margin: 0 0 10px;
            font-size: 19px;
            font-weight: 700;
            letter-spacing: -0.3px;
            color: #e2eefb;
          }

          .ml-feature-card p {
            margin: 0;
            color: #5a7a98;
            line-height: 1.68;
            font-size: 14.5px;
          }

          /* ── Process section ── */
          .ml-process-outer {
            position: relative;
          }

          .ml-process-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 0;
            position: relative;
          }

          .ml-process-card {
            position: relative;
            border-radius: 0;
            padding: 32px 28px;
            background: transparent;
            display: flex;
            flex-direction: column;
          }

          .ml-process-card:not(:last-child)::after {
            content: '';
            position: absolute;
            top: 48px;
            right: -1px;
            width: 2px;
            height: 32px;
            background: linear-gradient(180deg, rgba(45,212,191,0.4), transparent);
          }

          .ml-process-card-inner {
            border-radius: 18px;
            border: 1px solid rgba(255,255,255,0.06);
            background: linear-gradient(160deg, rgba(11,17,31,0.9), rgba(7,11,21,0.95));
            padding: 28px 24px;
            height: 100%;
            transition: border-color 0.25s ease, transform 0.2s ease;
            position: relative;
            overflow: hidden;
          }

          .ml-process-card-inner:hover {
            border-color: rgba(45,212,191,0.22);
            transform: translateY(-2px);
          }

          .ml-process-connector {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 8px;
            margin-top: -16px;
          }

          .ml-process-step-num {
            font-size: 52px;
            font-weight: 800;
            line-height: 1;
            letter-spacing: -3px;
            margin-bottom: 20px;
            background: linear-gradient(135deg, rgba(45,212,191,0.22) 0%, rgba(96,165,250,0.14) 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            display: block;
          }

          .ml-process-icon {
            width: 38px;
            height: 38px;
            border-radius: 11px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(45, 212, 191, 0.1);
            border: 1px solid rgba(45, 212, 191, 0.2);
            margin-bottom: 16px;
          }

          .ml-process-card-inner h3 {
            margin: 0 0 10px;
            font-size: 19px;
            font-weight: 700;
            letter-spacing: -0.3px;
            color: #ddeaf8;
          }

          .ml-process-card-inner p {
            margin: 0;
            color: #4e6880;
            line-height: 1.65;
            font-size: 14.5px;
          }

          .ml-process-grid-wrap {
            display: grid;
            grid-template-columns: 1fr 40px 1fr 40px 1fr;
            align-items: stretch;
            gap: 0;
          }

          .ml-process-arrow {
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 52px;
            color: rgba(45, 212, 191, 0.3);
          }

          .ml-showcase-wrap {
            margin-top: 24px;
            display: grid;
            grid-template-columns: 0.78fr 1.42fr;
            gap: 16px;
            align-items: stretch;
          }

          .ml-showcase-panel {
            border-radius: 18px;
            border: 1px solid rgba(118, 141, 170, 0.28);
            background: linear-gradient(180deg, rgba(11, 16, 29, 0.92), rgba(8, 12, 21, 0.94));
            box-shadow: 0 24px 42px rgba(0, 0, 0, 0.36);
            padding: 14px;
          }

          .ml-showcase-item {
            width: 100%;
            border: 1px solid transparent;
            background: transparent;
            color: #b2c4dd;
            text-align: left;
            border-radius: 12px;
            padding: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            font-family: inherit;
            margin-bottom: 8px;
          }

          .ml-showcase-item:hover {
            background: rgba(114, 144, 178, 0.14);
            color: #e5eefb;
          }

          .ml-showcase-item.active {
            background: linear-gradient(135deg, rgba(46, 212, 191, 0.18), rgba(59, 130, 246, 0.2));
            border-color: rgba(101, 176, 232, 0.55);
            color: #f3f8ff;
          }

          .ml-showcase-item h4 {
            margin: 0 0 6px;
            font-size: 16px;
            letter-spacing: -0.2px;
          }

          .ml-showcase-item p {
            margin: 0;
            color: inherit;
            opacity: 0.86;
            line-height: 1.55;
            font-size: 13px;
          }

          .ml-showcase-num {
            display: inline-grid;
            place-items: center;
            width: 24px;
            height: 24px;
            border-radius: 7px;
            margin-right: 8px;
            font-size: 12px;
            font-weight: 700;
            color: #0a2230;
            background: linear-gradient(135deg, #2dd4bf 0%, #60a5fa 100%);
          }

          .ml-showcase-screen {
            border-radius: 18px;
            border: 1px solid rgba(115, 138, 168, 0.32);
            background: linear-gradient(180deg, rgba(9, 14, 24, 0.95), rgba(6, 10, 18, 0.98));
            box-shadow: 0 30px 52px rgba(0, 0, 0, 0.43);
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .ml-screen-head {
            height: 40px;
            border-bottom: 1px solid rgba(104, 126, 156, 0.24);
            background: rgba(15, 21, 35, 0.72);
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 12px;
          }

          .ml-screen-dots {
            display: flex;
            gap: 6px;
          }

          .ml-screen-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #384c6a;
          }

          .ml-screen-title {
            font-size: 12px;
            color: #9ab1cd;
            letter-spacing: 0.3px;
          }

          .ml-screen-image-wrap {
            padding: 6px;
            background: linear-gradient(160deg, rgba(9, 15, 26, 0.94), rgba(7, 12, 21, 0.95));
          }

          .ml-screen-image {
            width: 100%;
            height: auto;
            display: block;
            object-fit: contain;
            border-radius: 8px;
            border: 1px solid rgba(111, 133, 160, 0.36);
            background: #0a1220;
            box-shadow: 0 14px 24px rgba(0, 0, 0, 0.35);
          }

          .ml-thumbs {
            border-top: 1px solid rgba(106, 126, 152, 0.24);
            padding: 10px;
            display: grid;
            grid-template-columns: repeat(6, minmax(0, 1fr));
            gap: 8px;
            background: rgba(9, 14, 24, 0.9);
          }

          .ml-thumb {
            border: 1px solid rgba(102, 126, 154, 0.32);
            background: #0c1525;
            border-radius: 8px;
            overflow: hidden;
            cursor: pointer;
            padding: 0;
            transition: transform 0.2s ease, border-color 0.2s ease;
          }

          .ml-thumb:hover {
            transform: translateY(-1px);
            border-color: rgba(134, 184, 224, 0.6);
          }

          .ml-thumb.active {
            border-color: rgba(116, 201, 255, 0.88);
            box-shadow: 0 0 0 1px rgba(116, 201, 255, 0.48);
          }

          .ml-thumb img {
            width: 100%;
            display: block;
            object-fit: cover;
            aspect-ratio: 16 / 10;
          }

          .ml-final {
            margin-top: 76px;
            border-radius: 20px;
            padding: 28px;
            border: 1px solid rgba(117, 140, 170, 0.32);
            background:
              radial-gradient(45% 80% at 0% 100%, rgba(46, 185, 255, 0.15), transparent 75%),
              radial-gradient(45% 80% at 100% 0%, rgba(40, 210, 140, 0.14), transparent 75%),
              rgba(8, 13, 24, 0.92);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            flex-wrap: wrap;
          }

          .ml-final h3 {
            margin: 0 0 8px;
            font-size: 28px;
            letter-spacing: -0.45px;
          }

          .ml-final p {
            margin: 0;
            color: #9bb0c9;
            font-size: 15px;
            line-height: 1.65;
          }


          .ml-faq-section {
            margin-top: 80px;
            margin-bottom: 76px;
          }

          .ml-faq-header {
            text-align: center;
            margin-bottom: 40px;
          }

          .ml-faq-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            padding: 6px 14px;
            border-radius: 999px;
            font-size: 12px;
            letter-spacing: 0.4px;
            font-weight: 600;
            color: #2DD4BF;
            background: rgba(45, 212, 191, 0.08);
            border: 1px solid rgba(45, 212, 191, 0.22);
            margin-bottom: 18px;
          }

          .ml-faq-list {
            max-width: 780px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .ml-faq-item {
            border-radius: 14px;
            border: 1px solid rgba(118, 140, 168, 0.22);
            background: linear-gradient(180deg, rgba(13, 19, 33, 0.88) 0%, rgba(9, 13, 23, 0.92) 100%);
            overflow: hidden;
            transition: border-color 0.25s ease;
          }

          .ml-faq-item.open {
            border-color: rgba(45, 212, 191, 0.35);
            background: linear-gradient(180deg, rgba(45, 212, 191, 0.05) 0%, rgba(9, 13, 23, 0.92) 100%);
          }

          .ml-faq-trigger {
            width: 100%;
            background: none;
            border: none;
            padding: 20px 22px;
            text-align: left;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            font-family: inherit;
          }

          .ml-faq-trigger:hover .ml-faq-q {
            color: #e8f1fb;
          }

          .ml-faq-q {
            font-size: 15.5px;
            font-weight: 600;
            color: #ccdaea;
            line-height: 1.4;
            letter-spacing: -0.1px;
            transition: color 0.2s;
          }

          .ml-faq-icon {
            flex-shrink: 0;
            width: 26px;
            height: 26px;
            border-radius: 8px;
            border: 1px solid rgba(100, 140, 185, 0.3);
            background: rgba(20, 30, 50, 0.7);
            display: grid;
            place-items: center;
            transition: all 0.25s ease;
          }

          .ml-faq-item.open .ml-faq-icon {
            background: rgba(45, 212, 191, 0.14);
            border-color: rgba(45, 212, 191, 0.4);
          }

          .ml-faq-icon svg {
            transition: transform 0.3s ease;
          }

          .ml-faq-item.open .ml-faq-icon svg {
            transform: rotate(45deg);
          }

          .ml-faq-body {
            overflow: hidden;
            max-height: 0;
            transition: max-height 0.35s ease, opacity 0.25s ease;
            opacity: 0;
          }

          .ml-faq-item.open .ml-faq-body {
            max-height: 300px;
            opacity: 1;
          }

          .ml-faq-answer {
            padding: 0 22px 20px;
            color: #8ba4be;
            font-size: 14.5px;
            line-height: 1.72;
            border-top: 1px solid rgba(100, 130, 165, 0.14);
            padding-top: 16px;
          }

          .ml-faq-view-all {
            margin-top: 28px;
            display: flex;
            justify-content: center;
          }

          .ml-faq-view-all-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 13px 28px;
            border-radius: 12px;
            font-size: 14.5px;
            font-weight: 600;
            cursor: pointer;
            font-family: inherit;
            transition: all 0.22s ease;
            border: 1px solid rgba(130, 160, 195, 0.35);
            background: rgba(10, 16, 28, 0.7);
            color: #c8d8ec;
            letter-spacing: 0.1px;
          }

          .ml-faq-view-all-btn:hover {
            border-color: rgba(45, 212, 191, 0.5);
            background: rgba(45, 212, 191, 0.07);
            color: #e5f2fb;
            transform: translateY(-1px);
          }

          @media (max-width: 1080px) {
            .ml-hero {
              grid-template-columns: 1fr;
              gap: 40px;
            }

            .ml-stat-band {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .ml-showcase-wrap {
              grid-template-columns: 1fr;
            }

            .ml-screen-image-wrap {
              padding: 8px;
            }

            .ml-process-grid-wrap {
              grid-template-columns: 1fr;
              gap: 12px;
            }

            .ml-process-arrow {
              display: none;
            }
          }

          @media (max-width: 760px) {
            .ml-shell {
              padding: 0 16px;
            }

            .ml-header {
              padding: 14px 16px !important;
            }

            .ml-header-nav-links {
              display: none !important;
            }

            .ml-header-email {
              display: none !important;
            }

            .ml-footer-grid {
              grid-template-columns: 1fr 1fr !important;
              padding: 36px 16px 24px !important;
              gap: 28px !important;
            }

            .ml-footer-bottom {
              padding: 14px 16px !important;
            }

            .ml-nav-row {
              height: auto;
              padding: 14px 0;
              align-items: flex-start;
              flex-direction: column;
            }

            .ml-feature-grid,
            .ml-stat-band {
              grid-template-columns: 1fr;
            }

            .ml-process-grid-wrap {
              grid-template-columns: 1fr;
              gap: 12px;
            }

            .ml-process-arrow {
              display: none;
            }

            .ml-thumbs {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }

            .ml-title {
              font-size: clamp(34px, 9.5vw, 52px);
              letter-spacing: -1.2px;
            }

            .ml-hero {
              padding: 40px 0 32px;
            }

            .ml-trust-row {
              gap: 12px;
            }

            .ml-sub {
              font-size: 15px;
              line-height: 1.65;
            }

            .ml-p {
              font-size: 15px;
            }

            .ml-section {
              margin-top: 60px;
            }

            .ml-section-header {
              margin-bottom: 28px;
            }

            .ml-faq-section {
              margin-top: 52px;
              margin-bottom: 48px;
            }

            .ml-faq-header {
              margin-bottom: 28px;
            }

            .ml-final {
              flex-direction: column;
              align-items: flex-start;
              padding: 24px 20px;
              gap: 16px;
            }

            .ml-final h3 {
              font-size: 22px;
            }

            .ml-final p {
              font-size: 14px;
            }

            .ml-kpi-value {
              font-size: 20px;
            }

            .ml-showcase-panel {
              padding: 10px;
            }

            .ml-showcase-item {
              padding: 10px;
            }

            .ml-showcase-item h4 {
              font-size: 14px;
            }

            .ml-h2 {
              font-size: clamp(22px, 6.5vw, 34px);
            }

            .ml-stat {
              padding: 14px 16px;
            }

            .ml-feature-card {
              padding: 22px 20px;
            }
          }

          @media (max-width: 480px) {
            .ml-footer-grid {
              grid-template-columns: 1fr !important;
            }

            .ml-cta-row {
              flex-direction: column;
              align-items: stretch;
            }

            .ml-cta-row .ml-btn,
            .ml-cta-row .ml-btn-primary {
              justify-content: center;
            }

            .ml-cta-row a {
              justify-content: center;
            }

            .ml-hero-visual {
              padding: 16px;
            }

            .ml-phone-mockup {
              display: none;
            }

            .ml-desktop-preview {
              transform: none;
            }

            .ml-process-card-inner {
              padding: 22px 18px;
            }
          }

          /* ── Hamburger button ── */
          .ml-hamburger {
            display: none;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 5px;
            width: 40px;
            height: 40px;
            border: 1px solid rgba(255,255,255,0.1);
            background: rgba(255,255,255,0.05);
            border-radius: 10px;
            cursor: pointer;
            padding: 0;
            transition: background 0.2s ease, border-color 0.2s ease;
            flex-shrink: 0;
          }

          .ml-hamburger:hover {
            background: rgba(255,255,255,0.09);
            border-color: rgba(255,255,255,0.18);
          }

          .ml-hamburger-line {
            width: 18px;
            height: 1.5px;
            background: #c8d8ec;
            border-radius: 2px;
            transition: transform 0.28s ease, opacity 0.2s ease;
            transform-origin: center;
          }

          .ml-hamburger.open .ml-hamburger-line:nth-child(1) {
            transform: rotate(45deg) translate(4.5px, 4.5px);
          }
          .ml-hamburger.open .ml-hamburger-line:nth-child(2) {
            opacity: 0;
            transform: scaleX(0);
          }
          .ml-hamburger.open .ml-hamburger-line:nth-child(3) {
            transform: rotate(-45deg) translate(4.5px, -4.5px);
          }

          /* ── Mobile menu overlay ── */
          .ml-mobile-menu {
            display: none;
            position: fixed;
            inset: 0;
            z-index: 9;
            pointer-events: none;
          }

          .ml-mobile-menu.open {
            pointer-events: all;
          }

          .ml-mobile-backdrop {
            position: absolute;
            inset: 0;
            background: rgba(0,0,0,0.55);
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .ml-mobile-menu.open .ml-mobile-backdrop {
            opacity: 1;
          }

          .ml-mobile-panel {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            padding-top: 68px;
            background: linear-gradient(180deg, rgba(5,7,14,0.99) 0%, rgba(8,11,20,0.99) 100%);
            backdrop-filter: blur(24px);
            border-bottom: 1px solid rgba(255,255,255,0.08);
            transform: translateY(-100%);
            transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .ml-mobile-menu.open .ml-mobile-panel {
            transform: translateY(0);
          }

          .ml-mobile-panel-inner {
            padding: 16px 20px 24px;
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .ml-mobile-nav-link {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: none;
            border: none;
            padding: 14px 16px;
            border-radius: 12px;
            color: #b0c4db;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            font-family: inherit;
            transition: background 0.18s ease, color 0.18s ease;
            text-align: left;
          }

          .ml-mobile-nav-link:hover {
            background: rgba(255,255,255,0.05);
            color: #e8f1fb;
          }

          .ml-mobile-divider {
            height: 1px;
            background: rgba(255,255,255,0.07);
            margin: 8px 0;
          }

          .ml-mobile-auth {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding-top: 4px;
          }

          .ml-mobile-auth-primary {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            border: none;
            background: linear-gradient(135deg, #1ecf97 0%, #2db9ff 100%);
            color: #021a12;
            padding: 14px;
            border-radius: 12px;
            font-size: 15px;
            font-weight: 700;
            cursor: pointer;
            font-family: inherit;
            letter-spacing: 0.15px;
            box-shadow: 0 4px 18px rgba(30,207,151,0.28), 0 8px 28px rgba(45,185,255,0.18);
            transition: filter 0.2s ease, transform 0.2s ease;
          }

          .ml-mobile-auth-primary:hover {
            filter: brightness(1.06);
            transform: translateY(-1px);
          }

          .ml-mobile-auth-secondary {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(143,165,190,0.25);
            color: #c4d4e8;
            padding: 13px;
            border-radius: 12px;
            font-size: 15px;
            font-weight: 500;
            cursor: pointer;
            font-family: inherit;
            transition: background 0.2s ease, border-color 0.2s ease;
          }

          .ml-mobile-auth-secondary:hover {
            background: rgba(255,255,255,0.07);
            border-color: rgba(143,165,190,0.45);
          }

          /* ── Desktop auth / header logo responsive ── */
          .ml-header-auth {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .ml-header-logo-box {
            width: 40px;
            height: 40px;
            border-radius: 11px;
            overflow: hidden;
            background-color: #0b0b0b;
            border: 1px solid #1f1f1f;
            box-shadow: 0 8px 20px rgba(0,0,0,0.6), 0 0 18px rgba(0,123,255,0.25);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          @media (max-width: 760px) {
            .ml-hamburger { display: flex; }
            .ml-header-auth { display: none; }
            .ml-mobile-menu { display: block; }
            .ml-header-logo-box {
              width: 36px;
              height: 36px;
              border-radius: 9px;
            }
            .ml-header-brand-name {
              font-size: 18px !important;
            }
          }
        `}
      </style>

      <header
        className="ml-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 32px',
          borderBottom: '1px solid #1a1a1a',
          backgroundColor: 'rgba(8, 8, 8, 0.92)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(10px)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="ml-header-logo-box">
            <img
              src={secondaryLogo}
              alt="TradeBetter Logo"
              style={{
                width: '50px',
                height: '50px',
                objectFit: 'cover',
                objectPosition: 'center',
                transform: 'scale(1.1) translateX(5px)',
                display: 'block'
              }}
            />
          </div>
          <span
            className="brandText ml-header-brand-name"
            style={{
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '0.6px',
              color: '#ffffff',
              textShadow: '0 0 16px rgba(0, 123, 255, 0.5)'
            }}
          >
            TradeBetter
          </span>
        </div>

        {/* Desktop nav */}
        <div className="ml-header-auth" style={{ gap: '4px' }}>
          <div className="ml-header-nav-links" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button className="ml-nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
            <button className="ml-nav-link" onClick={() => navigate('/faq')}>FAQ</button>
            <button className="ml-nav-link" onClick={() => navigate('/brokers')}>Brokers</button>
            <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 8px' }} />
          </div>

          {currentUser ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '4px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1ecf97 0%, #2db9ff 100%)',
                  color: '#021a12', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: 700, flexShrink: 0
                }}>
                  {(displayName || currentUser.email || '').charAt(0).toUpperCase()}
                </div>
                <span className="ml-header-email" style={{ fontSize: '13px', color: '#5a7290', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentUser.email}
                </span>
              </div>
              <button className="ml-nav-cta" onClick={() => navigate('/paywall')}>Subscribe</button>
              <button className="ml-nav-signout" onClick={async () => { await logout(); navigate('/login'); }}>Sign out</button>
            </>
          ) : (
            <>
              <button className="ml-nav-login" onClick={() => navigate('/login')}>Log in</button>
              <button className="ml-nav-cta" onClick={() => navigate('/login')}>
                Get started
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className={`ml-hamburger${mobileMenuOpen ? ' open' : ''}`}
          onClick={() => setMobileMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span className="ml-hamburger-line" />
          <span className="ml-hamburger-line" />
          <span className="ml-hamburger-line" />
        </button>
      </header>

      {/* Mobile slide-down menu */}
      <div className={`ml-mobile-menu${mobileMenuOpen ? ' open' : ''}`}>
        <div className="ml-mobile-backdrop" onClick={() => setMobileMenuOpen(false)} />
        <div className="ml-mobile-panel">
          <div className="ml-mobile-panel-inner">
            <button className="ml-mobile-nav-link" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}>
              Pricing
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
            <button className="ml-mobile-nav-link" onClick={() => { navigate('/faq'); setMobileMenuOpen(false); }}>
              FAQ
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
            <button className="ml-mobile-nav-link" onClick={() => { navigate('/brokers'); setMobileMenuOpen(false); }}>
              Brokers
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>

            <div className="ml-mobile-divider" />

            <div className="ml-mobile-auth">
              {currentUser ? (
                <>
                  <button className="ml-mobile-auth-primary" onClick={() => { navigate('/paywall'); setMobileMenuOpen(false); }}>
                    Subscribe to Pro
                  </button>
                  <button className="ml-mobile-auth-secondary" onClick={async () => { await logout(); navigate('/login'); setMobileMenuOpen(false); }}>
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <button className="ml-mobile-auth-primary" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                    Get started
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                  <button className="ml-mobile-auth-secondary" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                    Log in
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="ml-shell">
        <section className="ml-hero">

          {/* ── Left: copy ── */}
          <div className="ml-hero-center">
            <div className="ml-eyebrow">
              <div className="ml-eyebrow-avatars">
                <div className="ml-eyebrow-avatar" style={{ background: 'linear-gradient(135deg, #34d399 0%, #059669 100%)' }}>JM</div>
                <div className="ml-eyebrow-avatar" style={{ background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)' }}>AR</div>
                <div className="ml-eyebrow-avatar" style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)' }}>SK</div>
              </div>
              <div className="ml-eyebrow-stars">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="11" height="11" viewBox="0 0 24 24" fill="#f59e0b">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                ))}
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#c8d8ec', marginLeft: '4px' }}>4.9</span>
              </div>
              <span className="ml-eyebrow-text">Loved by options traders</span>
            </div>

            <h1 className="ml-title">
              <span className="ml-title-line1">Meet TradeBetter.</span>
              The journal that{' '}
              <span className="ml-title-accent">actually makes you better.</span>
            </h1>

            <p className="ml-sub">
              Import your options history, surface your real edge by setup and symbol,
              and get AI coaching that tells you exactly what to keep — and what's costing you.
            </p>

            <div className="ml-cta-row">
              <button className="ml-btn ml-btn-primary" onClick={() => navigate(currentUser ? '/paywall' : '/login')}>
                Get started free
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
              <a
                href="https://apps.apple.com/us/app/tradebetter/id6759767433"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: '#000',
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  transition: 'opacity 0.18s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              >
                <svg width="15" height="18" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.6)', fontWeight: 400, letterSpacing: '0.2px' }}>Download on the</span>
                  <span style={{ fontSize: '13px', color: '#fff', fontWeight: 700 }}>App Store</span>
                </div>
              </a>
            </div>

          </div>

          {/* ── Right: phone + desktop dual preview ── */}
          <div className="ml-hero-visual-col">
            <div className="ml-hero-dual-preview">

              {/* iOS phone mockup */}
              <div className="ml-phone-mockup">
                <div className="ml-phone-screen-wrap">
                  <div className="ml-phone-island" />
                  <img
                    src={iosScreenshotImg}
                    alt="TradeBetter iOS app"
                    className="ml-phone-screen"
                    loading="eager"
                  />
                </div>
                <div className="ml-phone-home-bar" />
              </div>

              {/* Desktop browser frame */}
              <div className="ml-desktop-preview">
                <img
                  src={dashboardOverviewImg}
                  alt="TradeBetter dashboard"
                  className="ml-hero-frame-img"
                  loading="eager"
                />
              </div>

            </div>
          </div>

        </section>

        <section className="ml-section">
          <h2 className="ml-h2">See the app in action</h2>
          <p className="ml-p">Explore the actual product flow, from overview to detailed review, in one interactive preview.</p>
          <div className="ml-showcase-wrap">
            <aside className="ml-showcase-panel">
              {screenshotItems.map((item, index) => (
                <button
                  key={item.title}
                  type="button"
                  className={`ml-showcase-item ${index === activeShotIndex ? 'active' : ''}`}
                  onClick={() => setActiveShotIndex(index)}
                >
                  <h4><span className="ml-showcase-num">{index + 1}</span>{item.title}</h4>
                  <p>{item.description}</p>
                </button>
              ))}
            </aside>

            <div className="ml-showcase-screen">
              <div className="ml-screen-head">
                <div className="ml-screen-dots">
                  <span className="ml-screen-dot" />
                  <span className="ml-screen-dot" />
                  <span className="ml-screen-dot" />
                </div>
                <div className="ml-screen-title">{activeShot.title}</div>
              </div>
              <div className="ml-screen-image-wrap">
                <img
                  src={activeShot.src}
                  alt={activeShot.title}
                  className="ml-screen-image"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.objectFit = 'contain';
                    event.currentTarget.style.padding = '28px';
                    event.currentTarget.src = primaryLogo;
                  }}
                />
              </div>

              <div className="ml-thumbs">
                {screenshotItems.map((item, index) => (
                  <button
                    key={item.title}
                    type="button"
                    className={`ml-thumb ${index === activeShotIndex ? 'active' : ''}`}
                    onClick={() => setActiveShotIndex(index)}
                    aria-label={`Show ${item.title}`}
                  >
                    <img src={item.src} alt={item.title} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="ml-stat-band">
          <div className="ml-stat">
            <strong>One place</strong>
            <span>Imports, dashboards, reviews, and notes in one workflow.</span>
          </div>
          <div className="ml-stat">
            <strong>Clear signals</strong>
            <span>Focus on actionable trends, not vanity metrics.</span>
          </div>
          <div className="ml-stat">
            <strong>Repeatable process</strong>
            <span>Turn random sessions into a consistent routine.</span>
          </div>
          <div className="ml-stat">
            <strong>Built to improve</strong>
            <span>Measure progress every day and every week.</span>
          </div>
        </section>

        <section className="ml-section">
          <div className="ml-section-header">
            <div className="ml-section-eyebrow">Why it works</div>
            <h2 className="ml-h2">One question answered four ways</h2>
            <p className="ml-p">
              Every part of TradeBetter is designed to surface the same signal fast: what should you do more of,
              and what should you cut out immediately?
            </p>
          </div>
          <div className="ml-feature-grid">
            {/* Import in minutes */}
            <article
              className="ml-feature-card"
              style={{ '--card-accent': 'linear-gradient(90deg, #60a5fa, #818cf8)' }}
            >
              <div className="ml-feature-icon" style={{ background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.22)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <h3>Import in minutes</h3>
              <p>Upload broker exports or sync supported brokers and start analyzing fast.</p>
            </article>

            {/* See your real edge */}
            <article
              className="ml-feature-card"
              style={{ '--card-accent': 'linear-gradient(90deg, #34d399, #2dd4bf)' }}
            >
              <div className="ml-feature-icon" style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.22)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3>See your real edge</h3>
              <p>Spot where your win rate and profitability are strongest by time, setup, and ticker.</p>
            </article>

            {/* Fix recurring mistakes */}
            <article
              className="ml-feature-card"
              style={{ '--card-accent': 'linear-gradient(90deg, #fb923c, #f87171)' }}
            >
              <div className="ml-feature-icon" style={{ background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.22)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fb923c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                  <line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
              </div>
              <h3>Fix recurring mistakes</h3>
              <p>Tag mistakes, review losing trades, and build a tighter process each week.</p>
            </article>

            {/* Trade with structure */}
            <article
              className="ml-feature-card"
              style={{ '--card-accent': 'linear-gradient(90deg, #a78bfa, #60a5fa)' }}
            >
              <div className="ml-feature-icon" style={{ background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.22)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <h3>Trade with structure</h3>
              <p>Run every session with data-backed feedback instead of emotion-driven guesses.</p>
            </article>
          </div>
        </section>

        <section className="ml-section">
          <div className="ml-section-header">
            <div className="ml-section-eyebrow">How it works</div>
            <h2 className="ml-h2">How traders use it every week</h2>
            <p className="ml-p">Simple loop. Import, review, adjust, repeat.</p>
          </div>
          <div className="ml-process-grid-wrap">

            {/* Step 1 */}
            <div className="ml-process-card-inner">
              <span className="ml-process-step-num">01</span>
              <div className="ml-process-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <h3>Import your history</h3>
              <p>Bring in your trades from broker files or account sync — no manual entry needed.</p>
            </div>

            {/* Arrow */}
            <div className="ml-process-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </div>

            {/* Step 2 */}
            <div className="ml-process-card-inner">
              <span className="ml-process-step-num">02</span>
              <div className="ml-process-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
              <h3>Review what matters</h3>
              <p>See daily and trade-level performance with clear context on what's actually driving results.</p>
            </div>

            {/* Arrow */}
            <div className="ml-process-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </div>

            {/* Step 3 */}
            <div className="ml-process-card-inner">
              <span className="ml-process-step-num">03</span>
              <div className="ml-process-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                  <polyline points="17 6 23 6 23 12"/>
                </svg>
              </div>
              <h3>Improve weekly</h3>
              <p>Use reports and AI reviews to sharpen decisions and set focused goals going forward.</p>
            </div>
          </div>
        </section>

        <section className="ml-final">
          <div>
            <h3>Ready to trade with clarity and consistency?</h3>
            <p>
              Start now, import your history, and make your next week in the market more structured than your last.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="ml-btn" onClick={() => navigate('/pricing')}>Compare plans</button>
            <button className="ml-btn ml-btn-primary" onClick={() => navigate(currentUser ? '/paywall' : '/login')}>Get started</button>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="ml-faq-section">
          <div className="ml-faq-header">
            <div className="ml-faq-eyebrow">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              FAQ
            </div>
            <h2 className="ml-h2">Questions we hear most</h2>
            <p className="ml-p" style={{ maxWidth: '520px', margin: '0 auto' }}>
              Everything you need to know before getting started — from imports and brokers to billing and data security.
            </p>
          </div>

          <div className="ml-faq-list">
            {visibleFaqs.map((item, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div key={index} className={`ml-faq-item${isOpen ? ' open' : ''}`}>
                  <button
                    className="ml-faq-trigger"
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                  >
                    <span className="ml-faq-q">{item.q}</span>
                    <span className="ml-faq-icon">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isOpen ? '#2DD4BF' : '#7a98b8'} strokeWidth="2.5" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </span>
                  </button>
                  <div className="ml-faq-body" aria-hidden={!isOpen}>
                    <p className="ml-faq-answer">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ml-faq-view-all">
            <button
              className="ml-faq-view-all-btn"
              onClick={() => navigate('/faq')}
            >
              View all FAQs
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
          </div>
        </section>

      </main>

      {/* ── Footer ── */}
      <footer style={{
        borderTop: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(5,7,12,0.95)',
        backdropFilter: 'blur(10px)',
        marginTop: '20px',
      }}>
        {/* Main footer grid */}
        <div className="ml-footer-grid" style={{
          maxWidth: '1320px',
          margin: '0 auto',
          padding: '56px 24px 40px',
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
          gap: '40px',
        }}>
          {/* Brand column */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '54px',
                height: '54px',
                borderRadius: '14px',
                overflow: 'hidden',
                backgroundColor: '#0b0b0b',
                border: '1px solid #1f1f1f',
                boxShadow: '0 8px 20px rgba(0,0,0,0.6), 0 0 18px rgba(0,123,255,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <img
                  src={secondaryLogo}
                  alt="TradeBetter Logo"
                  style={{
                    width: '66px',
                    height: '66px',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    transform: 'scale(1.1)',
                    display: 'block'
                  }}
                />
              </div>
              <span style={{ fontSize: '17px', fontWeight: 700, color: '#f2f7ff', letterSpacing: '0.2px' }}>TradeBetter</span>
            </div>
            <p style={{ color: '#5d7490', fontSize: '13.5px', lineHeight: 1.7, margin: '0 0 20px', maxWidth: '240px' }}>
              The trading journal built for serious options traders. Import, analyze, and improve.
            </p>
            {/* Badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(45,212,191,0.07)', border: '1px solid rgba(45,212,191,0.18)', borderRadius: '999px', padding: '6px 12px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#2DD4BF"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              <span style={{ color: '#2DD4BF', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.3px' }}>Pro AI-powered journaling</span>
            </div>
          </div>

          {/* Product column */}
          <div>
            <div style={{ color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '18px', opacity: 0.5 }}>Product</div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Features', action: () => {} },
                { label: 'Pricing', action: () => navigate('/pricing') },
                { label: 'FAQ', action: () => navigate('/faq') },
                { label: 'Sign in', action: () => navigate('/login') },
                { label: 'Get started', action: () => navigate(currentUser ? '/paywall' : '/login') },
              ].map(({ label, action }) => (
                <button key={label} onClick={action} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', color: '#5d7490', fontSize: '13.5px', cursor: 'pointer', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#c8d6e5'}
                  onMouseLeave={e => e.currentTarget.style.color = '#5d7490'}
                >{label}</button>
              ))}
            </nav>
          </div>

          {/* Legal column */}
          <div>
            <div style={{ color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '18px', opacity: 0.5 }}>Legal</div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Privacy Policy', action: () => navigate('/privacy') },
                { label: 'Terms of Service', action: () => navigate('/terms') },
              ].map(({ label, action }) => (
                <button key={label} onClick={action} style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', color: '#5d7490', fontSize: '13.5px', cursor: 'pointer', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#c8d6e5'}
                  onMouseLeave={e => e.currentTarget.style.color = '#5d7490'}
                >{label}</button>
              ))}
            </nav>
          </div>

          {/* Support column */}
          <div>
            <div style={{ color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '18px', opacity: 0.5 }}>Support</div>
            <p style={{ color: '#5d7490', fontSize: '13px', lineHeight: 1.65, margin: '0 0 14px' }}>
              Have a question or need help? We're here.
            </p>
            <a
              href="mailto:rsassanimarketing@gmail.com"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'rgba(45,212,191,0.06)',
                border: '1px solid rgba(45,212,191,0.18)',
                borderRadius: '8px',
                padding: '9px 14px',
                color: '#2DD4BF',
                fontSize: '13px',
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,212,191,0.12)'; e.currentTarget.style.borderColor = 'rgba(45,212,191,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(45,212,191,0.06)'; e.currentTarget.style.borderColor = 'rgba(45,212,191,0.18)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
              Email Support
            </a>
            <div style={{ color: '#3a4e62', fontSize: '11.5px', marginTop: '10px' }}>
              rsassanimarketing@gmail.com
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="ml-footer-bottom" style={{
          maxWidth: '1320px',
          margin: '0 auto',
          padding: '18px 24px',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <span style={{ color: '#3a4e62', fontSize: '12px' }}>© 2026 TradeBetter. All rights reserved.</span>
          <span style={{ color: '#3a4e62', fontSize: '12px' }}>Built for traders, by traders.</span>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLanding;
